import React, { useCallback, useEffect, useState } from 'react'
import Menu from '../../components/Menu/Menu'
import clouds from '../../assets/images/clouds-main.png'
import StakeDialog from '../../components/StakeDialog/StakeDialog';
import ConnectButton from '../../components/ConnectButton/ConnectButton';
import Button from '../../components/Button/Button';
import BigNumber from 'bignumber.js';

import { PuffLoader } from 'react-spinners';
import { useWallet } from '@binance-chain/bsc-use-wallet';
import { useInitialBlock } from '../../hooks/useInitialBlock';
import { MultiCall } from '../../types/MultiCall';
import { cactiNFTContract, cactiStakingContract, cactiTokenContract, getToken, multicallv2 } from '../../helpers/contracts';
import { getOwnedTokens, sendTransaction } from '../../helpers/config';
import { FarmingInfo } from '../../types/FarmingInfo';
import { showToast } from '../../helpers/toast';
import { numberWithCommas } from '../../helpers/helpers';

function Home() {
  const { account } = useWallet();
  const initialBlock = useInitialBlock();
  const [rewardsAreLoading, setRewardsAreLoading] = useState(true);
  const [approveIsLoading, setApproveIsLoading] = useState(false);
  const [confirmIsLoading, setConfirmIsLoading] = useState(false);
  const [harvestIsLoading, setHarvestIsLoading] = useState(false);
  const [isUnstaking, setIsUnstaking] = useState(false);
  const [inputValue, setInputValue] = useState(0);
  const [farmingInfo, setFarmingInfo] = useState<FarmingInfo>(
    {
      'NFTBalance': '0',
      'receipts': [],
      'contractERC20Balance': 0,
      'approved': false,
      'stakingEarned': 0,
      'ownedTokens': [],
      'endBlock': 0,
      'totalRewards': 0,
      'rewardsPerBlock': 0
    }
  );
  const [stakeDialogState, setStakeDialogState] = useState(false);

  const initFarm = useCallback(async () => {
    if (!account) {
      // Rewards per block
      const rewardsPerBlock = await cactiStakingContract.methods.rewardsPerBlock().call();
      farmingInfo.rewardsPerBlock = new BigNumber(rewardsPerBlock.toString()).div(1e18).toNumber();
      // Total Rewards
      const totalRewards = await cactiStakingContract.methods.totalRewards().call();
      farmingInfo.totalRewards = new BigNumber(totalRewards.toString()).div(1e18).toNumber();
      // End Block
      const endBlock = await cactiStakingContract.methods.endBlock().call();
      farmingInfo.endBlock = new BigNumber(endBlock).toNumber();

      setRewardsAreLoading(false);
    }

    if (account) {
      // NFTBalance
      const nftBalance = await cactiNFTContract.methods.balanceOf(account).call();

      // Contract ERC20 Balance
      const contractBalance = await cactiTokenContract.methods.balanceOf(getToken('CactiNFTStaking').address).call();

      // Multicall read functions [stakingPeriods, getStakedTokens, getAllowance];
      const farmingInfo: MultiCall[] = [
        { address: getToken('CactiNFTStaking').address, name: 'predictRewards', params: [account] },
        { address: getToken('CactiNFTStaking').address, name: 'getAllowance', params: [account] },
        { address: getToken('CactiNFTStaking').address, name: 'getStakedTokens', params: [account] },
        { address: getToken('CactiNFTStaking').address, name: 'endBlock', params: [] },
        { address: getToken('CactiNFTStaking').address, name: 'totalRewards', params: [] },
        { address: getToken('CactiNFTStaking').address, name: 'rewardsPerBlock', params: [] }
      ];
      const responseFarmingInfo = await multicallv2(getToken('CactiNFTStaking').abi, farmingInfo);

      // GET All owned tokens
      const baseUrl: string = getOwnedTokens(account, getToken('CactiNFT').address);
      const res = await fetch(baseUrl, { method: "GET", headers: { 'Content-Type': 'application/json', 'X-API-Key': 'no6HDlSVZeirkF85zzJ84g6T4gmIkf6ZIWodeck4QOYW9UD97f5unlRAxJabgYF4' } });
      const json = await res.json();
      let ownedTokens = [];
      if (json && json.result) {
        const result = json.result;
        for (let index = 0; index < result.length; index++) {
          ownedTokens.push(result[index].token_id);
        }
      }

      // GET Current stake earned based on all the staked NFT's
      let allReceipts = [];
      let stakingEarned = 0;
      for (let i = 0; i < responseFarmingInfo[2][0].length; i++) {
        const tokenId = parseInt(responseFarmingInfo[2][0][i].toString());
        const receipt = await cactiStakingContract.methods.receipt(tokenId).call();
        allReceipts.push(receipt);
      }
      for (let i = 0; i < allReceipts.length; i++) {
        stakingEarned += parseInt(allReceipts[i].totalRewards);
      }

      const data: FarmingInfo = {
        'NFTBalance': nftBalance,
        'receipts': allReceipts,
        'contractERC20Balance': new BigNumber(contractBalance).div(1e18).toNumber(),
        'approved': responseFarmingInfo[1][0],
        'stakingEarned': new BigNumber(stakingEarned).div(1e18).toNumber(),
        'ownedTokens': ownedTokens,
        'endBlock': responseFarmingInfo[3][0].toNumber(),
        'totalRewards': new BigNumber(responseFarmingInfo[4][0].toString()).div(1e18).toNumber(),
        'rewardsPerBlock': new BigNumber(responseFarmingInfo[5][0].toString()).div(1e18).toNumber()
      };
      setRewardsAreLoading(false);
      setFarmingInfo(data);
    }
  }, [account, farmingInfo])

  const approve = async () => {
    setApproveIsLoading(true);
    const func = await cactiNFTContract.methods.setApprovalForAll(getToken('CactiNFTStaking').address, true).encodeABI();

    try {
      await sendTransaction(account, getToken('CactiNFT').address, func, '0x0',
        async (tx: any) => {
          // Making sure the balance is being updated by doing 1 extra call to grab your NFT balance.
          const nftBalance = await cactiNFTContract.methods.balanceOf(account).call();
          farmingInfo.NFTBalance = nftBalance;
          initFarm();
          setFarmingInfo(farmingInfo);

          setApproveIsLoading(false);
          showToast('Succesfully approved!', false);
        },
        (error: any) => { // onError
          if (error.code === 4001) {
            showToast('Transaction signature has been denied', true);
            setApproveIsLoading(false);
          } else {
            showToast('Something went wrong, try again later...', true);
            setApproveIsLoading(false);
          }
        })
    } catch {
      setApproveIsLoading(false);
    }
  }

  const stake = async () => {
    // Make the call to stake
    setConfirmIsLoading(true);
    const func = await cactiStakingContract.methods.stakeNFT(farmingInfo.ownedTokens.slice(0, inputValue)).encodeABI();
    try {
      await sendTransaction(account, getToken('CactiNFTStaking').address, func, '0x0',
        async (tx: any) => {
          initFarm();
          setConfirmIsLoading(false);
          setStakeDialogState(false);
          showToast(`Succesfully deposited cacti's!`, false);
        },
        (error: any) => { // onError
          if (error.code === 4001) {
            showToast('Transaction signature has been denied', true);
            setStakeDialogState(false);
            setConfirmIsLoading(false);
          } else {
            showToast('Something went wrong, try again later...', true);
            setStakeDialogState(false);
            setConfirmIsLoading(false);
          }
        })
    } catch {
      setHarvestIsLoading(false);
      setConfirmIsLoading(false);
    }
  }

  const unstake = async () => {
    // Only unstake when the amount in contract is higher than the staking earned
    if (new BigNumber(farmingInfo.stakingEarned).isGreaterThanOrEqualTo(farmingInfo.contractERC20Balance)) {
      showToast('There is not enough balance in the contract', true);
      return;
    }

    let tokens = [];
    for (const recipe of farmingInfo.receipts) {
      tokens.push(recipe.tokenId.toString());
    }

    setConfirmIsLoading(true);
    const func = await cactiStakingContract.methods.unStakeNFT(tokens.slice(0, inputValue)).encodeABI();
    try {
      await sendTransaction(account, getToken('CactiNFTStaking').address, func, '0x0',
        async (tx: any) => {
          // Making sure the balance is being updated by doing 1 extra call to grab your NFT balance.
          const nftBalance = await cactiNFTContract.methods.balanceOf(account).call();
          farmingInfo.NFTBalance = nftBalance;
          setFarmingInfo(farmingInfo);

          initFarm();
          setConfirmIsLoading(false);
          setStakeDialogState(false)
          showToast(`Succesfully withdrawed cacti's!`, false);
        },
        (error: any) => { // onError
          if (error.code === 4001) {
            showToast('Transaction signature has been denied', true);
            setConfirmIsLoading(false);
            setStakeDialogState(false);
          } else {
            showToast('Something went wrong, try again later...', true);
            setConfirmIsLoading(false);
            setStakeDialogState(false);
          }
        })
    } catch {
      setConfirmIsLoading(false);
      setStakeDialogState(false);
    }
  }

  const harvest = async () => {
    // Only harvest when the amount in contract is higher than the staking earned
    if (new BigNumber(farmingInfo.stakingEarned).isGreaterThanOrEqualTo(farmingInfo.contractERC20Balance)) {
      showToast('There is not enough balance in the contract', true);
      return;
    }
    let tokens = [];
    for (const recipe of farmingInfo.receipts) {
      tokens.push(recipe.tokenId.toString());
    }

    setHarvestIsLoading(true);
    const func = await cactiStakingContract.methods.harvest(tokens).encodeABI();
    try {
      await sendTransaction(account, getToken('CactiNFTStaking').address, func, '0x0',
        async (tx: any) => {
          initFarm();
          setHarvestIsLoading(false);
          showToast(`Succesfully harvest tokens`, false);
        },
        (error: any) => { // onError
          if (error.code === 4001) {
            showToast('Transaction signature has been denied', true);
            setHarvestIsLoading(false)
          } else {
            showToast('Something went wrong, try again later...', true);
            setHarvestIsLoading(false)
          }
        })
    } catch {
      setHarvestIsLoading(false)
    }
  }

  // Added 3 blocks extra to make sure the block has been confirmed by blockchain as well.
  const farmingHasEnded = () => {
    return new BigNumber(initialBlock).isGreaterThanOrEqualTo(farmingInfo.endBlock);
  }

  useEffect(() => {
    if (initialBlock > 0 && !approveIsLoading) {
      initFarm();
    }
  }, [approveIsLoading, initialBlock])

  return (
    <React.Fragment>
      <div id="main-wrapper">
        <div id="bg-wrapper"></div>
        <Menu
          onClickCollection={() => { }}
          showCollection={false}
        />
        <div className="clouds-wrapper">
          <div className="clouds-1">
            <img src={clouds} alt="Cacti Club" />
          </div>
          <div className="clouds-2">
            <img src={clouds} alt="Cacti Club" />
          </div>
        </div>
        <div className="block-wrapper">
          {initialBlock <= 0 || isNaN(initialBlock)
            ? <div className='loader-wrap'>
              <PuffLoader color='#ffffff' />
              <h3>Connecting to Binance Smart Chain nodes...</h3>
              <p>Please have some patience. Refresh the page when it takes to long.</p>
            </div>
            :
            <div className="container">
              <div className="block-left">
                <div className="block-content block-text">
                  <h2>How it works</h2>
                  <p>Stake your Cacti NFT to earn the native Cacti token which will be utilized in our Cacti Showdown PVP game. Enter and exit the pool as you please. <b>21 Million CACTI</b> available to stake over the next <b>30 days</b>!</p>
                  <h2>GET YOUR HANDS ON A CACTI</h2>
                  <p>Cacti will be available for purchase on our <b><a href="https://mint.cacticlub.io/" target='_blank' rel="noreferrer">minting page</a></b> when minting is still live. Upon purchasing an NFT, a unique, blockchain-generated Cacti will be minted and delivered to your wallet. In the name of fairness, Cacti have no price tiers and are all initially priced at a flat rate of 0.4 BNB each.</p>
                  <span className="block-button">
                    <a className="button l-btn" href="https://mint.cacticlub.io/" target='_blank' rel="noreferrer">Get a Cacti</a>
                  </span>
                </div>
              </div>
              <div className="block-right">
                <div className="block-content block-input">
                  <div className="block-header">
                    <h2>{farmingHasEnded() ? 'Staking has ended' : 'Stake your Cacti'}</h2>
                  </div>
                  <div className="block-list">
                    <div className="block-line">
                      <div className="block-line-title">
                        Total Rewards
                      </div>
                      <div className="block-line-text">
                        {!rewardsAreLoading ? <><b>{numberWithCommas(farmingInfo.totalRewards)}</b> CACTI</> : 'Loading...'}
                      </div>
                    </div>
                    <div className="block-line">
                      <div className="block-line-title">
                        Est. Daily Reward
                      </div>
                      <div className="block-line-text">
                        {!rewardsAreLoading ? <><b>{numberWithCommas(new BigNumber(farmingInfo.rewardsPerBlock).times(28800).times(farmingInfo.receipts.length <= 0 ? 1 : farmingInfo.receipts.length).toFixed(0))}</b> CACTI</> : 'Loading...'}
                      </div>
                    </div>
                    <div className="block-line">
                      <div className="block-line-title">
                        Est. Monthly Reward
                      </div>
                      <div className="block-line-text">
                        {!rewardsAreLoading ? <><b>{numberWithCommas(new BigNumber(farmingInfo.rewardsPerBlock * 28800 * 365 / 12).times(farmingInfo.receipts.length <= 0 ? 1 : farmingInfo.receipts.length).toFixed(0).toString())}</b> CACTI</> : 'Loading...'}
                      </div>
                    </div>
                  </div>
                  <div className="block-info-button">
                    <div className="block-info-title">
                      CACTI Earned
                    </div>
                    <div className="block-info-line">
                      <div className="block-info-line-title">
                        {numberWithCommas(farmingInfo.stakingEarned.toFixed(0))}
                      </div>
                      <div className="block-info-line-btn">
                        <Button
                          isLoading={harvestIsLoading}
                          // className={`button ${new BigNumber(farmingInfo.stakingEarned).isLessThanOrEqualTo(0) ? 'l-btn' : 'btn-main'}`}
                          className={`button btn-main`}
                          onClick={() => harvest()}
                          // disabled={
                          //   new BigNumber(farmingInfo.stakingEarned).isLessThanOrEqualTo(0)
                          // }
                        >
                          Harvest
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="block-info-button last-info">
                    {(account && farmingInfo.approved) &&
                      <div className="block-info-title">
                        NFT's Staked
                      </div>
                    }
                    <div className="block-info-line">
                      {(account && farmingInfo.approved) &&
                        <div className="block-info-line-title">
                          {farmingInfo.receipts && farmingInfo.receipts && farmingInfo.receipts.length}
                        </div>
                      }
                      <div className={`block-info-line-btn ${(!account || !farmingInfo.approved) ? 'single-btn' : ''}`}>
                        {!account
                          ? <ConnectButton enableTextDialog className="button main-btn" />
                          : !farmingInfo.approved
                            ? <Button disabled={farmingHasEnded()} isLoading={approveIsLoading} className="button btn-main" onClick={() => approve()}><span>Approve</span></Button>
                            : farmingInfo.receipts.length <= 0
                              ? <Button
                                className={`button ${farmingHasEnded() || new BigNumber(farmingInfo.NFTBalance).isLessThanOrEqualTo(0) ? 'l-btn' : 'btn-main'}`}
                                disabled={
                                  farmingHasEnded() ||
                                  new BigNumber(farmingInfo.NFTBalance).isLessThanOrEqualTo(0)
                                }
                                onClick={() => setStakeDialogState(true)}>
                                <span>{farmingHasEnded() ? 'Staking has ended' : 'Stake'}</span>
                              </Button>
                              : <div className='block-info-add'>
                                <Button
                                  disabled={new BigNumber(farmingInfo.receipts.length).isLessThanOrEqualTo(0)}
                                  className={`button b-btn btn-main`}
                                  onClick={() => {
                                    setStakeDialogState(true);
                                    setIsUnstaking(true);
                                  }}
                                  icon={<i className="fa-thin fa-minus"></i>}
                                > </Button>
                                <Button
                                  className={`button ${farmingHasEnded() || new BigNumber(farmingInfo.NFTBalance).isLessThanOrEqualTo(0) ? 'l-btn' : 'b-btn btn-main'}`}
                                  onClick={() => {
                                    setStakeDialogState(true);
                                    setIsUnstaking(false);
                                  }}
                                  icon={<i className="fa-thin fa-plus"></i>}
                                  disabled={farmingHasEnded() || new BigNumber(farmingInfo.NFTBalance).isLessThanOrEqualTo(0)}
                                > </Button>
                              </div>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
      <StakeDialog
        open={stakeDialogState}
        onClose={() => setStakeDialogState(false)}
        isUnstaking={!isUnstaking}
        amountAvailable={isUnstaking ? farmingInfo.receipts.length : new BigNumber(farmingInfo.NFTBalance).toNumber()}
        onConfirm={() => isUnstaking ? unstake() : stake()}
        getInputValue={(value: number) => setInputValue(value)}
        confirmIsLoading={confirmIsLoading}
      />
    </React.Fragment>
  )
}

export default Home
